import React from "react";
import {Card, Tag, Button, Image, theme, Badge} from "antd";
import {PlusOutlined, CheckOutlined, EditOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {IProduct} from "../../../models/product/response";

interface Props {
    product: IProduct;
    storeId?: number;
    isSelected: boolean;
    onAdd: (product: IProduct) => void;
    onEdit: (product: IProduct) => void;
}

const ProductCard: React.FC<Props> = ({
                                          product,
                                          storeId,
                                          isSelected,
                                          onAdd,
                                          onEdit,
                                      }) => {
    const {t} = useTranslation();
    const {token} = theme.useToken();

    const calculateDiscountedPrice = (): number => {
        const {price, valueGeneralDiscount = 0, valueSpecialDiscount = 0} = product;
        const totalDiscount = valueGeneralDiscount + valueSpecialDiscount;
        return price - (price * totalDiscount) / 100;
    };

    const getStoreAvailability = (): { available: boolean; quantity: number } => {
        if (!storeId || !product.storeQuantities) {
            return {available: false, quantity: 0};
        }

        const storeQty = product.storeQuantities.find((sq) => sq.store_id === storeId);
        const factor = product.unit?.factor_accounting_system || 1;
        const actualQuantity = storeQty ? storeQty.quantity / factor : 0;

        return {
            available: storeQty?.available || false,
            quantity: actualQuantity,
        };
    };

    const discountedPrice = calculateDiscountedPrice();
    const hasDiscount = (product.valueGeneralDiscount || 0) + (product.valueSpecialDiscount || 0) > 0;
    const {available, quantity} = getStoreAvailability();

    const handleClick = () => {
        if (isSelected) {
            onEdit(product);
        } else {
            onAdd(product);
        }
    };

    return (
        <Badge.Ribbon
            text={isSelected ? t("selected") : undefined}
            color="green"
            style={{display: isSelected ? "block" : "none"}}
        >
            <Card
                hoverable
                style={{
                    height: "100%",
                    border: isSelected ? `2px solid ${token.colorSuccess}` : `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    boxShadow: isSelected ? `0 4px 12px ${token.colorSuccessBg}` : undefined,
                }}
                cover={
                    <div style={{
                        height: 200,
                        overflow: "hidden",
                        backgroundColor: token.colorBgLayout,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                    }}>
                        <Image
                            src={product.image?.url}
                            alt={product.name}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                            preview={true}
                            fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23f0f0f0' width='200' height='200'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='16'%3ENo Image%3C/text%3E%3C/svg%3E"
                        />
                    </div>
                }
            >
                <div style={{minHeight: 180}}>
                    {/* Product Name */}
                    <h4 style={{
                        margin: "0 0 12px 0",
                        fontSize: 16,
                        fontWeight: 600,
                        color: token.colorText,
                        minHeight: 48,
                        lineHeight: 1.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                    }}>
                        {product.name}
                    </h4>

                    {/* Unit */}
                    {product.unit && (
                        <div style={{
                            fontSize: 13,
                            color: token.colorTextSecondary,
                            marginBottom: 8,
                        }}>
                            {t("unit")}: <strong>{product.unit.name}</strong>
                        </div>
                    )}

                    {/* Price */}
                    <div style={{
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                    }}>
                        {hasDiscount ? (
                            <>
                                <span style={{
                                    fontSize: 13,
                                    textDecoration: "line-through",
                                    color: token.colorTextTertiary,
                                }}>
                                    {product.price?.toLocaleString()} {t("currency")}
                                </span>
                                <span style={{
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: token.colorSuccess,
                                }}>
                                    {discountedPrice.toLocaleString()} {t("currency")}
                                </span>
                            </>
                        ) : (
                            <span style={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: token.colorText,
                            }}>
                                {product.price?.toLocaleString()} {t("currency")}
                            </span>
                        )}
                    </div>

                    {/* Availability Tag */}
                    {storeId && (
                        <div style={{marginBottom: 12}}>
                            <Tag
                                color={available ? "success" : "error"}
                                style={{
                                    fontSize: 13,
                                    padding: "4px 12px",
                                    fontWeight: 500,
                                }}
                            >
                                {available
                                    ? `${t("available")}: ${quantity} ${product.unit?.name || ""}`
                                    : t("unavailable")}
                            </Tag>
                        </div>
                    )}

                    {/* Action Button */}
                    <Button
                        type={isSelected ? "default" : "primary"}
                        icon={isSelected ? <EditOutlined/> : <PlusOutlined/>}
                        onClick={handleClick}
                        block
                        size="large"
                        style={{
                            fontWeight: 500,
                            backgroundColor: isSelected ? token.colorSuccessBg : undefined,
                            borderColor: isSelected ? token.colorSuccess : undefined,
                            color: isSelected ? token.colorSuccess : undefined,
                        }}
                    >
                        {isSelected ? t("edit_quantity") : t("add")}
                    </Button>
                </div>
            </Card>
        </Badge.Ribbon>
    );
};

export default ProductCard;
